import React, { useCallback, useRef, useState } from "react";
import { DA_UserInformation } from "@danishagro/shared/src/interfaces/userInformation.interface";
import {
    DA_DynamicOverlayWrapper,
    useDynamicOverlay,
} from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Input } from "@danishagro/shared/src/components/atoms/Input/Input.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useOnSubmit } from "./hooks/useOnSubmit.hook";
import S from "./UserEditOverlay.module.scss";

interface Props extends DA_UserInformation {
    onSuccess: (data: DA_UserInformation) => void;
}

export const _UserEditOverlay = ({ onSuccess, ...props }: Props) => {
    const [errors, setErrors] = useState<string[]>([]);
    const { getDictionaryString } = useTranslations();
    const { isMobileOrTablet } = useScreen();
    const { closeOverlay } = useDynamicOverlay();
    const formRef = useRef<HTMLFormElement>(null);

    const { onSubmit, isSubmitting } = useOnSubmit({
        currentData: props,
        formRef,
        onSuccess,
        setErrors,
    });

    const onChange = useCallback(
        (name: string) => {
            const errorIndex = errors.indexOf(name);
            if (errorIndex > -1) {
                setErrors((current) => {
                    const newErrorArray = [...current];
                    newErrorArray.splice(errorIndex, 1);
                    return newErrorArray;
                });
            }
        },
        [errors]
    );

    return (
        <DA_DynamicOverlayWrapper
            content={
                <div className={S.wrapper}>
                    <div>
                        <DA_Input
                            label={getDictionaryString("profile.user.firstname")}
                            name="firstName"
                            defaultValue={props.firstName}
                            required
                            disabled={isSubmitting}
                            invalid={errors.includes("firstName")}
                            helperText={
                                errors.includes("firstName")
                                    ? getDictionaryString("profile.user.error.invalidName")
                                    : undefined
                            }
                            tooltip={getDictionaryString("required")}
                            onChange={() => onChange("firstName")}
                        />
                    </div>
                    <div>
                        <DA_Input
                            label={getDictionaryString("profile.user.lastname")}
                            name="lastName"
                            defaultValue={props.lastName}
                            required
                            disabled={isSubmitting}
                            invalid={errors.includes("lastName")}
                            helperText={
                                errors.includes("lastName")
                                    ? getDictionaryString("profile.user.error.invalidLastName")
                                    : undefined
                            }
                            tooltip={getDictionaryString("required")}
                            onChange={() => onChange("lastName")}
                        />
                    </div>
                    <div>
                        <DA_Input
                            label={getDictionaryString("profile.user.phone")}
                            type="tel"
                            name="phone"
                            defaultValue={props.phone}
                            disabled={isSubmitting}
                            invalid={errors.includes("phone")}
                            helperText={
                                errors.includes("phone")
                                    ? getDictionaryString("profile.user.error.invalidPhone")
                                    : undefined
                            }
                            onChange={() => onChange("phone")}
                        />
                    </div>
                    <div>
                        <DA_Input
                            label={getDictionaryString("profile.user.cellPhone")}
                            type="tel"
                            name="cellPhone"
                            defaultValue={props.cellPhone}
                            disabled={isSubmitting}
                            invalid={errors.includes("cellPhone")}
                            helperText={
                                errors.includes("cellPhone")
                                    ? getDictionaryString("profile.user.error.invalidCellPhone")
                                    : undefined
                            }
                            onChange={() => onChange("cellPhone")}
                        />
                    </div>
                </div>
            }
            footer={
                <div className={S.buttonWrapper}>
                    <DA_Button
                        title={getDictionaryString("general.cancel")}
                        isGhost
                        onClick={closeOverlay}
                        fullWidth={isMobileOrTablet}
                        disabled={isSubmitting}
                    />
                    <DA_Button
                        submit
                        title={getDictionaryString("general.saveInfo")}
                        fullWidth={isMobileOrTablet}
                        showSpinner={isSubmitting}
                    />
                </div>
            }
            wrapper={(children) => (
                <form ref={formRef} onSubmit={onSubmit}>
                    {children}
                </form>
            )}
        />
    );
};
